import React from "react";
import clsx from "clsx";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "../../../../ui/index.ts";
import { Card } from "../../../../shared/card/card.tsx";
import styles from "./retention-sent-view.module.css";

interface RetentionSentViewProps {
  loading?: boolean;
  onResign: () => void;
  /** When true, renders without outer Card (for use inside page modal). */
  updatedAt?: string | null;
  embedded?: boolean;
}

const FRIDAY = 5;
const TUESDAY = 2;

function getRetentionProceedAt(updatedAt: string | null | undefined): number | null {
  if (!updatedAt) return null;

  const updatedDate = new Date(updatedAt);
  const updatedTime = updatedDate.getTime();
  if (Number.isNaN(updatedTime)) return null;

  if (updatedDate.getDay() === FRIDAY) {
    const proceedDate = new Date(updatedDate);
    const daysUntilTuesday = (7 - FRIDAY) + TUESDAY;
    proceedDate.setDate(proceedDate.getDate() + daysUntilTuesday);
    return proceedDate.getTime();
  }

  const proceedDate = new Date(updatedDate);
  proceedDate.setDate(proceedDate.getDate() + 2);
  proceedDate.setHours(0, 0, 0, 0);
  return proceedDate.getTime();
}

export function RetentionSentView({
  loading = false,
  onResign,
  embedded = false,
  updatedAt,
}: RetentionSentViewProps) {
  const [now, setNow] = React.useState(() => Date.now());
  const proceedAt = React.useMemo(() => getRetentionProceedAt(updatedAt), [updatedAt]);
  const showProceedButton = proceedAt != null && now >= proceedAt;

  React.useEffect(() => {
    setNow(Date.now());
  }, [proceedAt]);

  React.useEffect(() => {
    if (proceedAt == null || now >= proceedAt) return;

    const delay = Math.max(proceedAt - now, 0);
    const timer = window.setTimeout(() => setNow(Date.now()), delay);

    return () => window.clearTimeout(timer);
  }, [now, proceedAt]);

  const availableAtLabel = React.useMemo(() => {
    if (proceedAt == null || showProceedButton) return null;

    return new Date(proceedAt).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, [proceedAt, showProceedButton]);

  const content = (
    <div className={styles.content}>
      <div className={styles.iconWrap} aria-hidden>
        <CheckCircle2 size={40} strokeWidth={1.75} />
      </div>
      <h2 className={clsx("heading-lg", styles.title)}>
        Offboarding Request Sent
      </h2>
      <p className={clsx("body-sm", styles.message)}>
        Thank You for sharing your concerns. The concerned team will review your
        request and connect with you soon to discuss the available options. We
        understand that this is an important decision. Please take the time you
        need before proceeding further.
      </p>

      {showProceedButton ? (
        <Button variant="danger-ghost" onClick={onResign} disabled={loading}>
          Proceed with Offboarding
          <ArrowRight size={16} />
        </Button>
      ) : availableAtLabel ? (
        <p className={clsx("body-sm", styles.hint)}>
          You may access offboarding request form after the completion of the next working day.
        </p>
      ) : null}
    </div>
  );

  if (embedded) {
    return content;
  }

  return <Card className={styles.card}>{content}</Card>;
}
